import * as React from 'react';

import {
  enumSalesLoadLineItemPhase,
  enumUnit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { FooterItem, MeasuredValue, Money } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import { useLineItemPhases } from '../../hooks/useLineItemPhases';
import { useLoadLineItemsTotals } from '../../hooks/useLoadLineItemsTotals';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { useTranslations } from '../../i18n/useTranslations';
import { LoadFormState } from '../../types/load';
import { convertWeightToDisplayValue } from '../../utils/weight';

import {
  $loadForm,
  $loadMaterialsStatus,
} from '~/modules/trade/_state/loadForm.state';
import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

type Props = {
  weightDisplayUnitPreference?: WeightDisplayUnit;
};

const loadMaterialsFormSelector = (state: LoadFormState) => ({
  lineItems: state.lineItems,
});

export const LoadMaterialsFooter: React.FC<Props> = ({
  weightDisplayUnitPreference,
}) => {
  const t = useTranslations();

  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadMaterialsFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const loadMaterialsStatus = useAtomValue($loadMaterialsStatus);
  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);

  const { lineItemsByPhase } = useLineItemPhases({
    lineItems: form.lineItems,
    loadPhaseType: loadMaterialsStatus,
  });

  const { getFooterDisplayUnit } = useWeightUnit({
    weightDisplayUnitPreference,
    defaultWeightDisplayUnit,
    facilityDefaultUnit,
  });

  const totals = useLoadLineItemsTotals({
    lineItems: lineItemsByPhase,
    facilityDefaultUnit,
    getFooterDisplayUnit,
  });

  const {
    itemsCount,
    requestedWeight,
    requestedEachCount,
    totalEstimatedPrice,
    pricingUnit,
  } = totals;

  const hasRequestedWeight =
    requestedWeight !== null && (requestedWeight?.value ?? 0) > 0;
  const hasRequestedEachCount = requestedEachCount > 0;

  if (
    loadMaterialsStatus !== enumSalesLoadLineItemPhase.Requested ||
    lineItemsByPhase.length === 0
  ) {
    return null;
  }

  const displayWeight = convertWeightToDisplayValue(
    requestedWeight,
    pricingUnit,
  );

  return (
    <>
      <FooterItem label={t.itemTable.salesOrderLoadFooter.materials}>
        <span className="text-lg font-bold tracking-tight">{itemsCount}</span>
      </FooterItem>

      <div className="h-9 w-px bg-neutral/10" />

      <FooterItem label={t.itemTable.salesOrderLoadFooter.totalWeight}>
        <div className="flex items-baseline gap-4">
          {hasRequestedWeight && (
            <div className="text-lg font-bold">
              <MeasuredValue value={displayWeight} unit={pricingUnit} />
            </div>
          )}
          {hasRequestedEachCount && (
            <div className="text-lg font-bold">
              <MeasuredValue value={requestedEachCount} unit={enumUnit.Each} />
            </div>
          )}
        </div>
      </FooterItem>

      <div className="h-9 w-px bg-neutral/10" />

      <FooterItem label={t.itemTable.salesOrderLoadFooter.total}>
        <span className="text-lg font-bold tracking-tight">
          <Money amount={totalEstimatedPrice} />
        </span>
      </FooterItem>
    </>
  );
};
